import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { CommonRootState } from '../combineReducers';
import { ISubscriptionPayload, SubscriptionStatus, SubscriptionType } from '../interfaces/subscription';
import { sendError } from '../sendError';
import { ThunkAction } from 'redux-thunk';
import i18next from 'i18next';

const fetchSubscriptionsRequest = () =>
  ({
    type: 'FETCH_SUBSCRIPTIONS_REQUEST'
  } as const);

export const fetchSubscriptionsSuccess = (payload: ISubscriptionPayload[]) =>
  ({
    type: 'FETCH_SUBSCRIPTIONS_SUCCESS',
    payload: payload.map(subscription => {
      const { expirationDate, status, type } = subscription;

      return {
        ...subscription,
        isExpiring:
          status === SubscriptionStatus.Active && moment(expirationDate).isBetween(moment(), moment().add(30, 'days')),
        isExpired: moment(expirationDate).isBefore(),
        typeDisplayName:
          type === SubscriptionType.PECConnect
            ? i18next.t('core.actions.pecConnect', 'PEC Connect')
            : i18next.t('core.actions.verificationPackage', 'Verification Package')
      };
    })
  } as const);

const fetchSubscriptionsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_SUBSCRIPTIONS_FAILURE',
    error
  } as const;
};

const shouldFetchSubscriptions = (state: CommonRootState) => {
  const {
    subscriptions: { isFetching, error, subscriptions }
  } = state;

  return !isFetching && !error && !subscriptions;
};

const fetchSubscriptions = (
  organizationId: string
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchSubscriptionsRequest());

    const response = await axios.get<{ value: ISubscriptionPayload[] }>('/api/v3.00/subscriptions', {
      params: {
        $filter: `subscriberId eq ${organizationId}`
      }
    });

    dispatch(fetchSubscriptionsSuccess(response.data.value));
  } catch (error) {
    dispatch(fetchSubscriptionsFailure(error));
  }
};

export const fetchSubscriptionsIfNeeded = (
  organizationId: string
): ThunkAction<void, CommonRootState, null, Actions> => (dispatch, getState) => {
  if (shouldFetchSubscriptions(getState())) {
    dispatch(fetchSubscriptions(organizationId));
  }
};

export type Actions =
  | ReturnType<typeof fetchSubscriptionsRequest>
  | ReturnType<typeof fetchSubscriptionsSuccess>
  | ReturnType<typeof fetchSubscriptionsFailure>;
